import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { getAuth } from "firebase/auth";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

const COLORS = [
  { label: "Bleu",   value: "BLUE",   hex: "#185FA5" },
  { label: "Violet", value: "PURPLE", hex: "#534AB7" },
  { label: "Vert",   value: "GREEN",  hex: "#276749" },
  { label: "Orange", value: "ORANGE", hex: "#C05621" },
  { label: "Rouge",  value: "RED",    hex: "#9B2C2C" },
];

async function sendCommand(tableId, command) {
  await addDoc(collection(db, "esp"), {
    table: parseInt(tableId),
    command,
    processed: false,
    createdAt: serverTimestamp(),
  });
}

function ResponsiveGrid({ children, cols = 3 }) {
  const [gridCols, setGridCols] = useState(1);
  useEffect(() => {
    const update = () => setGridCols(window.innerWidth >= 560 ? cols : 1);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [cols]);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${gridCols}, minmax(0,1fr))`, gap: 12, marginBottom: 16 }}>
      {children}
    </div>
  );
}

function SectionLabel({ label, t }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: t.border }} />
    </div>
  );
}

export default function PomodoroPage() {
  const uid      = getAuth().currentUser?.uid;
  const navigate = useNavigate();

  const [tableNum,  setTableNum]  = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [dark,      setDark]      = useState(() => localStorage.getItem("ws-theme") === "dark");

  const [studyMin,  setStudyMin]  = useState(25);
  const [breakMin,  setBreakMin]  = useState(5);
  const [slots,     setSlots]     = useState(4);
  const [autoBreak, setAutoBreak] = useState(false);
  const [color,     setColor]     = useState("BLUE");

  const [sending,   setSending]   = useState(false);
  const [phase,     setPhase]     = useState("idle");
  const [cmdKey,    setCmdKey]    = useState(null);

  const toggleTheme = () => {
    setDark(d => { localStorage.setItem("ws-theme", !d ? "dark" : "light"); return !d; });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    async function fetchTable() {
      if (!uid) { setLoading(false); return; }
      try {
        const q    = query(collection(db, "visiteur"), where("FirebaseId", "==", uid), where("sortie", "==", false));
        const snap = await getDocs(q);
        if (!snap.empty) setTableNum(snap.docs[0].data().table);
      } catch(e) { console.error(e); }
      setLoading(false);
    }
    fetchTable();
  }, [uid]);

  async function cmd(command, key) {
    setCmdKey(key); setSending(true);
    try { await sendCommand(tableNum, command); }
    catch(e) { console.error(e); }
    setSending(false); setCmdKey(null);
  }

  async function handleStart() {
    const auto = autoBreak ? "AUTO" : "MANUAL";
    await cmd(`START:${studyMin}:${breakMin}:${slots}:${auto}:${color}`, "start");
    setPhase("running");
  }

  async function handleColorChange(c) {
    setColor(c);
    if (phase === "running") await cmd(`COLOR:${c}`, "color");
  }

  async function handlePause()  { await cmd("PAUSE",  "pause");  }
  async function handleResume() { await cmd("RESUME", "resume"); }
  async function handleStop()   { await cmd("STOP",   "stop");   setPhase("idle"); }

  const totalMin = studyMin * slots + breakMin * Math.max(0, slots - 1);

  if (loading) return (
    <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spin size="large" />
    </div>
  );

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 700, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 40, objectFit: "contain" }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>Pomodoro</div>
              <div style={{ fontSize: 11, color: t.text2 }}>
                {tableNum ? `Table ${tableNum}` : "Aucune session active"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← Retour
            </button>
          </div>
        </div>

        <div style={{ padding: "20px 18px 0" }}>

          {/* No session */}
          {!tableNum && (
            <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "40px 20px", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text1, marginBottom: 6 }}>Aucune session active</div>
              <div style={{ fontSize: 13, color: t.text2 }}>Tu dois avoir une session active à WeStudy pour utiliser le Pomodoro.</div>
            </div>
          )}

          {tableNum && (
            <>
              {/* Status card */}
              <div style={{
                background: phase === "running" ? t.greenBg : t.cardBg,
                border: `1px solid ${phase === "running" ? t.green : t.border}`,
                borderRadius: 14, padding: "14px 16px", marginBottom: 20,
                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: t.blueBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>⏱️</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.text1 }}>
                      {phase === "running" ? "Session en cours" : "Prêt à démarrer"}
                    </div>
                    <div style={{ fontSize: 11, color: t.text2, marginTop: 2 }}>Table {tableNum}</div>
                  </div>
                </div>

                {/* Control buttons — wrap on mobile */}
                {phase === "running" && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button onClick={handlePause} disabled={sending} style={{
                      ...s.actionBtn, background: t.bg3, border: `1px solid ${t.border}`,
                      color: t.text1, opacity: cmdKey === "pause" ? 0.6 : 1,
                    }}>
                      {cmdKey === "pause" ? "..." : "⏸ Pause"}
                    </button>
                    <button onClick={handleResume} disabled={sending} style={{
                      ...s.actionBtn, background: t.green, color: "#fff", border: "none",
                      opacity: cmdKey === "resume" ? 0.6 : 1,
                    }}>
                      {cmdKey === "resume" ? "..." : "▶ Reprendre"}
                    </button>
                    <button onClick={handleStop} disabled={sending} style={{
                      ...s.actionBtn, background: t.red, color: "#fff", border: "none",
                      opacity: cmdKey === "stop" ? 0.6 : 1,
                    }}>
                      {cmdKey === "stop" ? "..." : "⏹ Arrêter"}
                    </button>
                  </div>
                )}
              </div>

              {/* Config section */}
              <SectionLabel label="Configuration" t={t} />

              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>

                {/* Inputs — responsive grid */}
                <ResponsiveGrid cols={3}>
                  {[
                    { label: "Travail (min)", val: studyMin, set: setStudyMin, min: 1, max: 120 },
                    { label: "Pause (min)",   val: breakMin, set: setBreakMin, min: 1, max: 60  },
                    { label: "Sessions",      val: slots,    set: setSlots,    min: 1, max: 10  },
                  ].map(({ label, val, set, min, max }) => (
                    <div key={label}>
                      <div style={{ fontSize: 11, color: t.text2, fontWeight: 500, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                      <input
                        type="number" min={min} max={max} value={val}
                        onChange={e => set(Math.max(min, parseInt(e.target.value) || min))}
                        disabled={phase === "running"}
                        style={{
                          width: "100%", padding: "10px 12px", fontSize: 20, fontWeight: 700,
                          border: `1px solid ${t.border}`, borderRadius: 10,
                          color: t.text1, background: phase === "running" ? t.bg3 : t.cardBg,
                          boxSizing: "border-box", textAlign: "center",
                          opacity: phase === "running" ? 0.5 : 1,
                        }}
                      />
                    </div>
                  ))}
                </ResponsiveGrid>

                {/* Summary */}
                <div style={{ background: t.bg3, borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                  {[
                    { label: "Travail",  val: `${studyMin} min` },
                    { label: "Pause",    val: `${breakMin} min` },
                    { label: "Sessions", val: `${slots}x` },
                    { label: "Total",    val: `${totalMin} min` },
                  ].map(({ label, val }) => (
                    <div key={label} style={{ textAlign: "center", minWidth: 60 }}>
                      <div style={{ fontSize: 10, color: t.text3, fontWeight: 500, textTransform: "uppercase" }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: t.text1, marginTop: 2 }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Auto break toggle */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${t.border}` }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: t.text1 }}>Pause automatique</div>
                    <div style={{ fontSize: 11, color: t.text2, marginTop: 2 }}>passe en pause sans confirmation</div>
                  </div>
                  <div
                    onClick={() => phase !== "running" && setAutoBreak(a => !a)}
                    style={{
                      width: 40, height: 22, borderRadius: 22,
                      cursor: phase === "running" ? "default" : "pointer", flexShrink: 0,
                      background: autoBreak ? t.blue : t.border,
                      position: "relative", transition: "background 0.2s",
                      opacity: phase === "running" ? 0.5 : 1,
                    }}
                  >
                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: "white", position: "absolute", top: 3, left: autoBreak ? 21 : 3, transition: "left 0.2s" }} />
                  </div>
                </div>
              </div>

              {/* Color section */}
              <SectionLabel label="Couleur de l'écran" t={t} />

              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, marginBottom: 20 }}>
                <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                  {COLORS.map(c => (
                    <div key={c.value} onClick={() => handleColorChange(c.value)} title={c.label} style={{
                      width: 36, height: 36, borderRadius: "50%", background: c.hex, cursor: "pointer",
                      outline: color === c.value ? `3px solid ${c.hex}` : "3px solid transparent",
                      outlineOffset: 2, transition: "outline 0.15s", flexShrink: 0,
                    }} />
                  ))}
                  <div style={{ fontSize: 12, color: t.text2 }}>
                    {COLORS.find(c => c.value === color)?.label}
                  </div>
                </div>
              </div>

              {/* Launch / Reconfigure button */}
              {phase === "idle" ? (
                <button onClick={handleStart} disabled={sending} style={{
                  width: "100%", padding: "14px", borderRadius: 12, border: "none",
                  background: sending ? t.bg3 : t.blue,
                  color: sending ? t.text3 : "#fff",
                  fontSize: 15, fontWeight: 600,
                  cursor: sending ? "not-allowed" : "pointer",
                  opacity: sending ? 0.7 : 1, transition: "all 0.2s",
                }}>
                  {cmdKey === "start" ? "Envoi en cours..." : "⏱ Lancer sur l'écran"}
                </button>
              ) : (
                <button onClick={handleStop} disabled={sending} style={{
                  width: "100%", padding: "12px", borderRadius: 12,
                  border: `1px solid ${t.border}`,
                  background: t.bg3, color: t.text2,
                  fontSize: 14, fontWeight: 500, cursor: "pointer",
                }}>
                  Reconfigurer
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4",
    red: "#dc2626", cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16",
    red: "#f87171", cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob:  { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn:  { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  actionBtn:   { fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 10, cursor: "pointer", transition: "opacity 0.15s", whiteSpace: "nowrap" },
};